import * as React from "react";
import { inject, observer } from "mobx-react";
import * as _ from "lodash";
import { Col, Row } from "react-bootstrap";
import {
    AcceptedMimetype,
    AttachmentStore,
    FileSystemDirectoryEntry,
    FileSystemEntry,
    FileSystemFileEntry,
} from "Stores/attachmentStore";
import { toJS } from "mobx";
import { CommonStore } from "Stores/commonStore";
import { Utils } from "../../common/utils";

const BYTES_IN_MEGAYTES = 1e+6;
const ERROR_FADE_OUT_MILLIS = 5000;

export interface AttachmentFile extends File {
    sanitizedName: string
}

interface AttachmentChipProps {
    attachment: AttachmentFile
    index: number
    onDelete: (idx: number) => void
}

class AttachmentChip extends React.Component<AttachmentChipProps> {

    handleClick = () => {
        this.props.onDelete(this.props.index);
    };

    render(){
        return (
            <div className="item-chip">
                <div>{ this.props.attachment.sanitizedName }</div>
                <i className="icon icon-times" onClick={ this.handleClick }/>
            </div>
        );
    }
}

interface AttachmentSelectorProps {
    attachmentStore?: AttachmentStore
    commonStore?: CommonStore
    attachments: AttachmentFile[]
    onChange: (files: AttachmentFile[]) => void
    dropRef: any
}

@inject("attachmentStore", "commonStore")
@observer
export class AttachmentSelector extends React.Component<AttachmentSelectorProps> {


    filesRef;
    dirInputRef;
    errorContainerRef;
    dragBorderTimeout;

    hasAttachedEventListeners = false;

    errorContainerTimeout: number;

    componentDidMount(){
        this.props.attachmentStore!.getAcceptedMimetypes();
    }

    componentWillUnmount(){
        if(this.props.dropRef){
            document.removeEventListener("dragenter", this.handleDragEnter);
            document.removeEventListener("dragleave", this.handleDragLeave);
            document.removeEventListener("dragover", this.handleDragOver);
            document.removeEventListener("drop", this.handleDrop);
        }
    }

    attachEventListeners = () => {
        this.hasAttachedEventListeners = true;

        document.addEventListener("dragenter", this.handleDragEnter);
        // Need the dragleave event in order to catch drag event leaving the document
        document.addEventListener("dragleave", this.handleDragLeave);
        document.addEventListener("dragover", this.handleDragOver);
        document.addEventListener("drop", this.handleDrop);
    };

    removeDropzoneBorder = () => {
        this.props.dropRef.classList.remove("drop-zone");
    };

    handleDragEnter = (ev) => {
        ev.preventDefault();
    };

    handleDragLeave = (ev) => {
        ev.preventDefault();
        if(!this.props.dropRef.contains(ev.target)){
            this.removeDropzoneBorder();
        }
    };

    handleDragOver = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if(this.props.dropRef.contains(ev.target)){
            this.props.dropRef.classList.add("drop-zone");
            clearTimeout(this.dragBorderTimeout);
            this.dragBorderTimeout = setTimeout(this.removeDropzoneBorder, 200);
        } else {
            this.removeDropzoneBorder();
        }
        return false;

    };

    handleDrop = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if(this.props.dropRef.contains(ev.target)){
            this.onDropHandler(ev);
        }
        return false;
    };

    clearHasErrors = () => {
        this.props.dropRef.classList.remove("drop-zone-error");
    };

    addError = (msg: string) => {
        clearTimeout(this.errorContainerTimeout);
        this.props.dropRef.classList.add("drop-zone-error");
        this.errorContainerTimeout = window.setTimeout(this.clearHasErrors, ERROR_FADE_OUT_MILLIS);

        const elem = document.createElement("div");
        elem.innerText = msg;
        this.errorContainerRef.appendChild(elem);
        setTimeout(() => {
            elem.remove();
        }, ERROR_FADE_OUT_MILLIS);

    };

    validateFileName = (fileName: string): boolean => {
        // dot files (e.g .DS_STORE) are ignored
        // Get just the characters before the file extension then trim whitespace off
        return !!fileName.substring(0, fileName.lastIndexOf(".")).trim();
    };

    validateFileType = (file: File) => {
        let isValid = false;
        if(file.type){
            isValid = _.findIndex(this.props.attachmentStore!.acceptedMimetypes, {mimetype: file.type}) > -1;
        } else {
            // Naively checks if filetype is one of accepted types by checking the extension in the name
            // or return true if unable to load accepted mimetypes setting
            if(!this.props.attachmentStore!.mimetypesRegex || this.props.attachmentStore!.mimetypesRegex!.test(file.name)){
                isValid = true;
            }
        }

        if(!isValid){
            this.addError(`Unable to upload ${file.name}. File type is unsupported`);
        }
        return isValid;
    };

    validateFileSize = (file: File) => {
        if(file.size < this.props.commonStore!.settings.attachments.max_file_size_mb * BYTES_IN_MEGAYTES){
            return true;
        }
        this.addError(`File ${ file.name } is larger than ${ this.props.commonStore!.settings.attachments.max_file_size_mb }MB limit`);
        return false;
    };

    basicFileCheck = (file: File): boolean => {
        return !!(this.validateFileName(file.name) && this.validateFileType(file) && this.validateFileSize(file));
    };

    fileToAttachmentFile = (file: File): AttachmentFile => {
        const ret = file;
        (file as AttachmentFile).sanitizedName = Utils.sanitizeFileName(file.name);
        return ret as AttachmentFile;
    };

    showAttachmentSelector = () => {
        this.filesRef!.click();
    };

    handleDelete = (idx: number) => {
        const files = this.props.attachments.slice();
        files.splice(idx, 1);
        this.props.onChange(files);
    };

    handleFilesChange = (ev) => {
        ev.preventDefault();
        const filesFromInput = this.filesRef!.files;
        const files: AttachmentFile[] = [...this.props.attachments];
        for(let idx = 0; idx < filesFromInput.length; idx++){
            const file = filesFromInput[idx];
            if(this.basicFileCheck(file)){
                files.push(this.fileToAttachmentFile(file));
            }
        }
        this.props.onChange(files);
    };

    filePromiseFromEntry = (entry: FileSystemFileEntry): Promise<File> => {
        return new Promise((resolve) => {
            entry.file(resolve);
        });
    };

    getSubentries = async (entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> => {
        const reader = entry.createReader();
        return await new Promise(resolve => {
            reader.readEntries(resolve);
        });
    };

    recurse = async (entry: FileSystemEntry): Promise<File[]> => {
        if(entry.isFile){
            // We're done, return the FileSystemEntry as a single element array of File
            const fileSystemEntry = await entry as FileSystemFileEntry;
            return [await (this.filePromiseFromEntry(fileSystemEntry))];
        }
        // We've got a directory, need to loop and concat
        const subDirContents = await this.getSubentries(entry as FileSystemDirectoryEntry) as FileSystemEntry[];
        const mapped: File[][] = await Promise.all((subDirContents).map(this.recurse));
        return _.flatten(mapped);
    };

    onDropHandler = async (ev: any) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.props.dropRef.classList.remove("drop-zone");

        let files: File[] = [];
        if(ev.type === "drop"){
            if(ev.dataTransfer.items){
                // File System API is supported, can recurse through directories
                const items = ev.dataTransfer.items;
                const attachmentFiles: (File | File[])[] = [];
                // make a copy of dataTransfer.items as fileSystemEntries
                //   chrome will dispose of the dataTransfer event once the dropHandler exits
                // https://bugs.chromium.org/p/chromium/issues/detail?id=137231

                const entries: FileSystemEntry[] = [];
                for(let idx = 0; idx < items.length; idx++){
                    if("webkitGetAsEntry" in items[idx]){
                        // If the item has webkitGetAsEntry, we need to process it
                        entries.push(items[idx].webkitGetAsEntry());
                    } else{
                        // Otherwise it's ready to be added
                        attachmentFiles.push(items[idx]);
                    }
                }

                for(let idx = 0; idx < entries.length; idx++){
                    attachmentFiles.push(await this.recurse(entries[idx]));
                }
                files = _.flatten(attachmentFiles);
            } else if(ev.dataTransfer.files){
                // File System API not supported, ignore directories
                files = ev.dataTransfer.files;
                // if size == 0 and type is not set, it is probably a directory
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                files = _.filter(files, (file: File) => file.size > 0 && file.type);
                if(files.length !== ev.dataTransfer.files.length){
                    // Add one error message if any directories are included
                    this.addError("Your browser doesn't support folder drag and drop");
                }
            }
        } else if(ev.type === "change"){
            files = ev.target.files;
        }

        if(!!files){
            const attachments: AttachmentFile[] = _.map(_.filter(files, this.basicFileCheck), this.fileToAttachmentFile);
            this.props.onChange([...this.props.attachments, ...attachments]);
        }
        return false;
    };

    assignRef = ref => this.filesRef = ref;

    assignDirInputRef = ref => this.dirInputRef = ref;

    assignErrorContainerRef = ref => this.errorContainerRef = ref;

    render(){
        if(!this.hasAttachedEventListeners && this.props.dropRef !== undefined){
            this.attachEventListeners();
        }

        if(!this.props.attachmentStore || !this.props.attachmentStore.acceptedMimetypes){
            return null;
        }

        return (
            <Col className="attachment-selector flex-md-fill no-gutters align-items-center">
                <input
                    ref={ this.assignDirInputRef }
                    style={ {display: "none"} }
                    type="file"
                    onChange={ this.onDropHandler }
                />
                <input
                    id="file-select"
                    type="file"
                    accept={ toJS(this.props.attachmentStore.acceptedMimetypes).map((am: AcceptedMimetype) => `.${ am.extension }`).join(",") }
                    onChange={ this.handleFilesChange }
                    multiple={ true }
                    ref={ this.assignRef }
                    style={ {display: "none"} }
                />
                { this.props.attachments.length > 0
                    ? <div className="selected-container"><Row>{ this.props.attachments.map((file: AttachmentFile, i: number) =>
                        (<AttachmentChip key={ i } attachment={ file } onDelete={ this.handleDelete } index={ i }/>),
                    ) }</Row></div>
                    : null }
                <div className="attachment-info align-items-center">
                    <i className="icon icon-paperclip"/>
                    Drag and drop files or&nbsp;<span onClick={ this.showAttachmentSelector }>browse</span>
                </div>
                <Col className="attachments-error-container" ref={ this.assignErrorContainerRef }/>
            </Col>
        );
    }
}